import { useAuth, usePermissions } from '@/hooks/useAuth'
import { useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Button } from '@/components/ui/button'
import { MessageSquare, X } from 'lucide-react'
import { useChatPanel } from './ChatContext'
import { ChatNav } from './ChatNav'
import { ChatThread } from './ChatThread'
import { cn } from '@/lib/utils'

export function ChatWidget() {
  const { isAuthenticated, profile } = useAuth()
  const { hasPermission } = usePermissions()
  const { isOpen, closeChat, openChat, selectedPrescriptionId } = useChatPanel()
  const canUseChat =
    isAuthenticated && !!profile?.active && hasPermission('use_prescription_chat')
  const chatUnread = useQuery(
    api.prescriptionChat.getUnreadTotal,
    canUseChat ? {} : 'skip'
  )

  if (!canUseChat) return null

  const badgeCount = chatUnread ?? 0

  return (
    <>
      {!isOpen && (
        <button
          type="button"
          onClick={() => openChat()}
          className={cn(
            'fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full',
            'bg-primary text-white shadow-lg transition hover:bg-primary/90',
            'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary'
          )}
          aria-label="Apri chat"
        >
          <MessageSquare className="h-6 w-6" />
          {badgeCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {badgeCount > 99 ? '99+' : badgeCount}
            </span>
          )}
        </button>
      )}

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/20 sm:hidden"
            onClick={closeChat}
            aria-hidden
          />
          <aside
            className={cn(
              'fixed bottom-3 left-3 right-3 z-50 flex h-[75dvh] max-h-[600px] flex-col overflow-hidden',
              'rounded-2xl border border-gray-200 bg-white shadow-2xl',
              'sm:bottom-5 sm:left-auto sm:right-5 sm:h-[600px] sm:max-h-[calc(100dvh-6rem)] sm:w-[400px]'
            )}
            role="dialog"
            aria-label="Chat prescrizioni"
          >
            <div className="flex items-center justify-between border-b px-3 py-2">
              <div className="flex items-center gap-2 font-semibold text-gray-900">
                <MessageSquare className="h-4 w-4" />
                Chat
                {badgeCount > 0 && !selectedPrescriptionId && (
                  <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-semibold text-white">
                    {badgeCount}
                  </span>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={closeChat} aria-label="Chiudi chat">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="min-h-0 flex-1">
              {selectedPrescriptionId ? (
                <ChatThread key={selectedPrescriptionId} />
              ) : (
                <ChatNav />
              )}
            </div>
          </aside>
        </>
      )}
    </>
  )
}
